import React, { useState, useEffect, useCallback } from 'react';
import { Plus, FileText, Link as LinkIcon, AlertCircle, Trash2, Lock, Unlock, Eye, EyeOff, FolderInput, Archive } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { api } from '../../api/client';
import Modal from '../shared/Modal';
import PasswordGate from '../shared/PasswordGate';
import MoveToWorkshopModal from '../shared/MoveToWorkshopModal';
import ArchivedSessionsPanel from './ArchivedSessionsPanel';

// Writing Sessions tab within a single Workshop. The "link to foresight
// session" dropdown is restricted to foresight sessions inside this same
// workshop (the backend also enforces this).
const WorkshopWritingTab = ({ workshop, onNavigateToWritingSession }) => {
  const { currentUser } = useAppContext();
  const [writingSessions, setWritingSessions] = useState([]);
  const [foresightSessions, setForesightSessions] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionDescription, setNewSessionDescription] = useState('');
  const [linkedForesightSession, setLinkedForesightSession] = useState('');
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [newSessionPassword, setNewSessionPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [pendingSession, setPendingSession] = useState(null);
  const [managingSession, setManagingSession] = useState(null);
  const [managePassword, setManagePassword] = useState('');
  const [showManagePassword, setShowManagePassword] = useState(false);
  const [movingSession, setMovingSession] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'facilitator';

  const loadData = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const [writing, foresight] = await Promise.all([
        api.getWritingSessions(workshop.id),
        api.getSessions(workshop.id),
      ]);
      setWritingSessions(writing);
      setForesightSessions(foresight);
    } catch (error) {
      console.error('Failed to load writing sessions:', error);
    } finally {
      setIsLoadingList(false);
    }
  }, [workshop.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSessionClick = (session) => {
    if (session.is_password_protected && !isAdmin) {
      setPendingSession(session);
    } else {
      onNavigateToWritingSession(session);
    }
  };

  const createWritingSession = async () => {
    if (!newSessionName) return;
    setIsLoading(true);
    try {
      const newSession = await api.createWritingSession({
        name: newSessionName,
        description: newSessionDescription,
        workshop_id: workshop.id,
        foresight_session_id: linkedForesightSession ? parseInt(linkedForesightSession) : null,
        is_password_protected: isPasswordProtected,
        session_password: isPasswordProtected ? newSessionPassword : null,
      });
      setWritingSessions([...writingSessions, newSession]);
      setShowCreateModal(false);
      resetCreateForm();
      onNavigateToWritingSession(newSession);
    } catch (error) {
      alert('Failed to create writing session: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetCreateForm = () => {
    setNewSessionName('');
    setNewSessionDescription('');
    setLinkedForesightSession('');
    setIsPasswordProtected(false);
    setNewSessionPassword('');
    setShowPassword(false);
  };

  const handleDeleteWritingSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this writing session? This cannot be undone.')) return;
    try {
      await api.deleteWritingSession(sessionId);
      setWritingSessions(writingSessions.filter(s => s.id !== sessionId));
    } catch (error) {
      alert('Failed to delete writing session: ' + error.message);
    }
  };

  const handleOpenManage = (session, e) => {
    e.stopPropagation();
    setManagingSession(session);
    setManagePassword(session.session_password || '');
    setShowManagePassword(false);
  };

  const handleSavePasswordSettings = async () => {
    const isProtected = managePassword.trim().length > 0;
    try {
      const updated = await api.updateWritingSession(managingSession.id, {
        is_password_protected: isProtected,
        session_password: isProtected ? managePassword.trim() : null,
      });
      setWritingSessions(writingSessions.map(s => s.id === updated.id ? updated : s));
      setManagingSession(null);
    } catch (error) {
      alert('Failed to update password settings: ' + error.message);
    }
  };

  const handleRemovePassword = async () => {
    if (!window.confirm('Remove password protection from this session?\n\nAll participants will be able to access it without a password.')) return;
    try {
      const updated = await api.updateWritingSession(managingSession.id, {
        is_password_protected: false,
        session_password: null,
      });
      setWritingSessions(writingSessions.map(s => s.id === updated.id ? updated : s));
      setManagingSession(null);
    } catch (error) {
      alert('Failed to remove password: ' + error.message);
    }
  };

  return (
    <div>
      {pendingSession && (
        <PasswordGate
          title={pendingSession.name}
          verify={async (password) => {
            const result = await api.verifyWritingSessionPassword(pendingSession.id, password);
            return result.valid;
          }}
          onUnlocked={() => { const s = pendingSession; setPendingSession(null); onNavigateToWritingSession(s); }}
          onCancel={() => setPendingSession(null)}
        />
      )}

      {managingSession && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-base font-bold text-gray-900 mb-1">Session Password</h3>
            <p className="text-sm text-gray-500 mb-4">"{managingSession.name}"</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password <span className="text-gray-400 font-normal">(leave blank to remove protection)</span>
            </label>
            <div className="relative mb-4">
              <input
                type={showManagePassword ? 'text' : 'password'}
                value={managePassword}
                onChange={e => setManagePassword(e.target.value)}
                placeholder="Enter a password…"
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              />
              <button onClick={() => setShowManagePassword(!showManagePassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showManagePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setManagingSession(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
                Cancel
              </button>
              {managingSession.is_password_protected && (
                <button onClick={handleRemovePassword}
                  className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 text-sm font-medium">
                  Remove
                </button>
              )}
              <button onClick={handleSavePasswordSettings}
                className="flex-1 px-4 py-2 bg-[#01006A] text-white rounded-lg hover:bg-[#010059] text-sm font-medium">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Writing Sessions</h2>
        <div className="flex gap-2">
          {currentUser.role === 'admin' && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`flex items-center space-x-2 border px-4 py-2 rounded-lg ${
                showArchived ? 'bg-[#01006A] text-white border-[#01006A]' : 'text-gray-600 border-gray-300 hover:border-[#01006A] hover:text-[#01006A]'
              }`}
            >
              <Archive className="w-4 h-4" />
              <span>{showArchived ? 'Hide Archived' : 'Show Archived'}</span>
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 bg-[#01006A] text-white px-4 py-2 rounded-lg hover:bg-[#010059]">
              <Plus className="w-5 h-5" />
              <span>New Writing Session</span>
            </button>
          )}
        </div>
      </div>

      {showArchived && (
        <div className="mb-6">
          <ArchivedSessionsPanel
            workshopId={workshop.id}
            fetchArchived={api.getArchivedWritingSessions}
            onRestore={api.restoreWritingSession}
            onPurge={api.purgeWritingSession}
            itemLabel="writing session"
          />
        </div>
      )}

      {isLoadingList ? (
        <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {writingSessions.map(session => {
            // Moving is unchanged -- creator or admin. Deletion is admin-only
            // now (facilitators can create/move sessions but never delete them).
            const canMove = currentUser.role === 'admin' || session.created_by === currentUser.id;
            const canDelete = currentUser.role === 'admin';
            return (
              <div key={session.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer relative group"
                onClick={() => handleSessionClick(session)}>

                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {canMove && (
                    <button onClick={(e) => { e.stopPropagation(); setMovingSession(session); }}
                      className="p-1.5 text-gray-400 hover:text-[#01006A] rounded hover:bg-gray-100" title="Move to another workshop">
                      <FolderInput className="w-4 h-4" />
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={(e) => handleOpenManage(session, e)}
                      title={session.is_password_protected ? 'Manage password' : 'Add password'}
                      className={`p-1.5 rounded hover:bg-gray-100 ${session.is_password_protected ? 'text-amber-500' : 'text-gray-400'}`}>
                      {session.is_password_protected ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={(e) => handleDeleteWritingSession(session.id, e)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-gray-100" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-6 h-6 text-[#01006A] flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-gray-900">{session.name}</h3>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    {session.is_password_protected && (
                      <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                        <Lock className="w-3 h-3" />
                      </span>
                    )}
                    {session.foresight_session_id && (
                      <LinkIcon className="w-4 h-4 text-[#01006A]" title="Linked to foresight session" />
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{session.description}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{new Date(session.created_at).toLocaleDateString()}</span>
                  <span className={`px-2 py-1 rounded ${session.status === 'active' ? 'bg-[#c0c0e6] text-[#010059]' : 'bg-gray-100 text-gray-700'}`}>
                    {session.status}
                  </span>
                </div>
              </div>
            );
          })}

          {writingSessions.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No writing sessions in this workshop yet.</p>
            </div>
          )}
        </div>
      )}

      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); resetCreateForm(); }} title="Create Writing Session">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Session Name</label>
            <input type="text" value={newSessionName} onChange={e => setNewSessionName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              placeholder="Q1 2025 Scenario Synthesis" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea value={newSessionDescription} onChange={e => setNewSessionDescription(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              rows="3" placeholder="Collaborative session to synthesize workshop findings..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Link to Foresight Session (Optional)</label>
            <select value={linkedForesightSession} onChange={e => setLinkedForesightSession(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]">
              <option value="">None -- Standalone Session</option>
              {foresightSessions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <p className="text-xs text-gray-400 mt-1">Only foresight sessions within this workshop can be linked.</p>
          </div>
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={isPasswordProtected} onChange={e => setIsPasswordProtected(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-[#01006A] focus:ring-[#01006A]" />
              <span className="text-sm font-medium text-gray-700">Password-protect this session</span>
            </label>
            {isPasswordProtected && (
              <div className="relative mt-2">
                <input type={showPassword ? 'text' : 'password'} value={newSessionPassword}
                  onChange={e => setNewSessionPassword(e.target.value)}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
                  placeholder="Set a password for participants" />
                <button onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            )}
          </div>
          <div className="flex space-x-3">
            <button onClick={createWritingSession} disabled={!newSessionName || isLoading || (isPasswordProtected && !newSessionPassword)}
              className="flex-1 bg-[#01006A] text-white py-2 rounded-lg hover:bg-[#010059] disabled:bg-gray-300">
              {isLoading ? 'Creating...' : 'Create'}
            </button>
            <button onClick={() => { setShowCreateModal(false); resetCreateForm(); }}
              className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300">
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {movingSession && (
        <MoveToWorkshopModal
          itemName={movingSession.name}
          currentWorkshopId={workshop.id}
          onMove={(targetWorkshopId) => api.moveWritingSession(movingSession.id, targetWorkshopId)}
          onMoved={() => setWritingSessions(writingSessions.filter(s => s.id !== movingSession.id))}
          onClose={() => setMovingSession(null)}
        />
      )}
    </div>
  );
};

export default WorkshopWritingTab;
